import React, { useState, useEffect } from 'react';
import { useAuth } from '@/App';
import axios from 'axios';
import { API } from '@/App';
import { toast } from 'sonner';
import { Terminal, Zap, Trophy, Shield } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

const Landing = () => {
  const { login } = useAuth();
  const [showAuth, setShowAuth] = useState(false);
  const [isLogin, setIsLogin] = useState(true);
  const [formData, setFormData] = useState({ email: '', password: '', username: '' });
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);

  // Load Google API on component mount
  useEffect(() => {
    // Dynamically load Google SDK
    if (!window.google) {
      const script = document.createElement('script');
      script.src = 'https://accounts.google.com/gsi/client';
      script.async = true;
      script.defer = true;
      document.head.appendChild(script);
    }
  }, []);

  const handleGoogleSuccess = async (response) => {
    setGoogleLoading(true);
    try {
      // Send Google token to backend for verification and user creation/login
      const res = await axios.post(`${API}/auth/google`, {
        token: response.credential
      });
      login(res.data.token, res.data.user);
      toast.success('Logged in with Google successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Google authentication failed');
      console.error('Google auth error:', error);
    } finally {
      setGoogleLoading(false);
    }
  };

  const handleGoogleError = () => {
    toast.error('Google login failed');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const endpoint = isLogin ? '/auth/login' : '/auth/register';
      const payload = isLogin 
        ? { email: formData.email, password: formData.password }
        : formData;
      
      const response = await axios.post(`${API}${endpoint}`, payload);
      login(response.data.token, response.data.user);
      toast.success(isLogin ? 'Welcome back!' : 'Account created successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Authentication failed');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  if (showAuth) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-6">
        <div className="absolute inset-0 bg-gradient-to-b from-primary/5 to-transparent pointer-events-none" />
        
        <div className="relative w-full max-w-md">
          <div className="glass-panel rounded-xl p-8 border border-white/10" data-testid="auth-form">
            <div className="text-center mb-8">
              <Terminal className="w-12 h-12 text-primary mx-auto mb-4" />
              <h2 className="text-3xl font-grotesk font-bold text-white mb-2">
                {isLogin ? 'SYSTEM ACCESS' : 'INITIALIZE NODE'}
              </h2>
              <p className="text-zinc-400 text-sm font-mono uppercase tracking-wider">
                {isLogin ? 'Enter credentials' : 'Create account'}
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              {!isLogin && (
                <div>
                  <Label htmlFor="username" className="text-zinc-300 text-sm mb-2 block font-mono uppercase tracking-wide">Username</Label>
                  <Input
                    id="username"
                    name="username"
                    data-testid="auth-username-input"
                    value={formData.username}
                    onChange={handleChange}
                    required={!isLogin}
                    className="bg-black/50 border-zinc-800 focus:border-primary focus:ring-1 focus:ring-primary/50 text-white"
                  />
                </div>
              )}
              
              <div>
                <Label htmlFor="email" className="text-zinc-300 text-sm mb-2 block font-mono uppercase tracking-wide">Email</Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  data-testid="auth-email-input"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="bg-black/50 border-zinc-800 focus:border-primary focus:ring-1 focus:ring-primary/50 text-white"
                />
              </div>
              
              <div>
                <Label htmlFor="password" className="text-zinc-300 text-sm mb-2 block font-mono uppercase tracking-wide">Password</Label>
                <Input
                  id="password"
                  name="password"
                  type="password"
                  data-testid="auth-password-input"
                  value={formData.password}
                  onChange={handleChange}
                  required
                  className="bg-black/50 border-zinc-800 focus:border-primary focus:ring-1 focus:ring-primary/50 text-white"
                />
              </div>

              <Button 
                type="submit" 
                data-testid="auth-submit-button"
                disabled={loading}
                className="w-full bg-primary text-black font-bold hover:bg-primary/90 hover:shadow-green-glow uppercase tracking-wide h-12 text-sm"
              >
                {loading ? 'PROCESSING...' : (isLogin ? 'ACCESS SYSTEM' : 'CREATE NODE')}
              </Button>
            </form>

            {/* Google OAuth Button */}
            <div className="mt-6">
              <div className="relative mb-6">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-zinc-700" />
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-2 bg-black/50 text-zinc-500 font-mono text-xs uppercase">or</span>
                </div>
              </div>

              <button
                onClick={() => {
                  window.google?.accounts.id.renderButton(
                    document.getElementById('googleButton'),
                    {
                      theme: 'dark',
                      size: 'large',
                      width: '100%',
                      locale: 'en'
                    }
                  );
                }}
                className="w-full bg-white text-black font-bold hover:bg-gray-100 rounded-lg h-12 text-sm flex items-center justify-center gap-2 transition"
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24">
                  <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                Sign in with Google
              </button>
            </div>

            <div id="googleButton" className="absolute hidden"></div>

            <div className="mt-6 text-center">
              <button
                onClick={() => setIsLogin(!isLogin)}
                data-testid="auth-toggle-button"
                className="text-zinc-400 hover:text-primary text-sm font-mono"
              >
                {isLogin ? 'Need an account? Initialize node' : 'Already have access? Login'}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background relative overflow-hidden">
      {/* Hero background */}
      <div className="absolute inset-0 bg-gradient-to-b from-primary/10 via-transparent to-transparent pointer-events-none" />
      <div 
        className="absolute inset-0 opacity-10" 
        style={{
          backgroundImage: `url('https://images.unsplash.com/photo-1770520216996-d800d20df61b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzOTB8MHwxfHNlYXJjaHwxfHxhYnN0cmFjdCUyMGJsb2NrY2hhaW4lMjBuZXR3b3JrJTIwZGFyayUyMGdyZWVufGVufDB8fHx8MTc3Mzc1OTg2Nnww&ixlib=rb-4.1.0&q=85')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      />

      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-6 py-12">
        {/* Header */}
        <header className="flex justify-between items-center mb-24">
          <div className="flex items-center space-x-2">
            <Terminal className="w-8 h-8 text-primary" />
            <span className="text-2xl font-grotesk font-bold text-white">BLOCKCHAIN QUEST</span>
          </div>
        </header>

        {/* Hero Section */}
        <div className="max-w-4xl mx-auto text-center mb-24" data-testid="hero-section">
          <div className="mb-6">
            <span className="text-xs font-mono uppercase tracking-widest text-primary/80">Level Up Your Blockchain Knowledge</span>
          </div>
          <h1 className="text-5xl md:text-7xl font-grotesk font-bold tracking-tighter leading-none text-white mb-6">
            Master The
            <span className="block text-primary text-glow mt-2">Decentralized Future</span>
          </h1>
          <p className="text-base md:text-lg text-zinc-400 leading-relaxed mb-10 max-w-2xl mx-auto">
            Learn blockchain technology through an immersive, gamified experience. Complete 45 chapters across three levels, solve puzzles, compete in battles, and become a blockchain expert.
          </p>
          <Button 
            onClick={() => setShowAuth(true)}
            data-testid="get-started-button"
            className="bg-primary text-black font-bold hover:bg-primary/90 hover:shadow-green-glow uppercase tracking-wide text-sm px-8 py-6 text-base h-auto rounded-sm"
          >
            <Zap className="w-5 h-5 mr-2" />
            INITIALIZE QUEST
          </Button>
        </div>

        {/* Features Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-24">
          <div className="bg-zinc-900/30 border border-white/10 rounded-xl p-8 hover:border-primary/30 hover:-translate-y-1 hover:shadow-2xl hover:shadow-primary/10 group" data-testid="feature-45-chapters">
            <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mb-4 group-hover:bg-primary/20">
              <Terminal className="w-6 h-6 text-primary" />
            </div>
            <h3 className="text-2xl md:text-3xl font-grotesk font-semibold text-white mb-3 tracking-tight">45 Chapters</h3>
            <p className="text-zinc-400 leading-relaxed">Progress from beginner to advanced through carefully crafted lessons covering every aspect of blockchain technology.</p>
          </div>

          <div className="bg-zinc-900/30 border border-white/10 rounded-xl p-8 hover:border-primary/30 hover:-translate-y-1 hover:shadow-2xl hover:shadow-primary/10 group" data-testid="feature-8-games">
            <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mb-4 group-hover:bg-primary/20">
              <Trophy className="w-6 h-6 text-primary" />
            </div>
            <h3 className="text-2xl md:text-3xl font-grotesk font-semibold text-white mb-3 tracking-tight">8 Game Modes</h3>
            <p className="text-zinc-400 leading-relaxed">Test your knowledge through matching games, quiz battles, detective mysteries, and more interactive challenges.</p>
          </div>

          <div className="bg-zinc-900/30 border border-white/10 rounded-xl p-8 hover:border-primary/30 hover:-translate-y-1 hover:shadow-2xl hover:shadow-primary/10 group" data-testid="feature-leaderboards">
            <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mb-4 group-hover:bg-primary/20">
              <Shield className="w-6 h-6 text-primary" />
            </div>
            <h3 className="text-2xl md:text-3xl font-grotesk font-semibold text-white mb-3 tracking-tight">Compete & Earn</h3>
            <p className="text-zinc-400 leading-relaxed">Climb the leaderboards, earn XP, unlock achievements, and prove your blockchain expertise to the world.</p>
          </div>
        </div>

        {/* Learning Levels */}
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl md:text-5xl font-grotesk font-bold text-white text-center mb-12 tracking-tight">Your Learning Path</h2>
          <div className="space-y-4">
            <div className="glass-panel rounded-xl p-6 flex items-center justify-between hover:border-primary/30 group" data-testid="level-beginner">
              <div>
                <span className="text-xs font-mono uppercase tracking-widest text-primary/80 mb-1 block">Level 01</span>
                <h3 className="text-xl font-grotesk font-semibold text-white group-hover:text-primary">Beginner</h3>
                <p className="text-sm text-zinc-400 mt-1">15 chapters • Fundamentals & Core Concepts</p>
              </div>
              <div className="text-4xl font-mono text-primary/30 group-hover:text-primary">01</div>
            </div>
            
            <div className="glass-panel rounded-xl p-6 flex items-center justify-between hover:border-primary/30 group" data-testid="level-intermediate">
              <div>
                <span className="text-xs font-mono uppercase tracking-widest text-primary/80 mb-1 block">Level 02</span>
                <h3 className="text-xl font-grotesk font-semibold text-white group-hover:text-primary">Intermediate</h3>
                <p className="text-sm text-zinc-400 mt-1">15 chapters • Smart Contracts & DeFi</p>
              </div>
              <div className="text-4xl font-mono text-primary/30 group-hover:text-primary">02</div>
            </div>
            
            <div className="glass-panel rounded-xl p-6 flex items-center justify-between hover:border-primary/30 group" data-testid="level-advanced">
              <div>
                <span className="text-xs font-mono uppercase tracking-widest text-primary/80 mb-1 block">Level 03</span>
                <h3 className="text-xl font-grotesk font-semibold text-white group-hover:text-primary">Advanced</h3>
                <p className="text-sm text-zinc-400 mt-1">15 chapters • Security & Future Tech</p>
              </div>
              <div className="text-4xl font-mono text-primary/30 group-hover:text-primary">03</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Landing;
